/**
 * Automatic Free Gift
 * Auto-adds a designated gift variant to the cart once the subtotal
 * (excluding the gift itself) reaches FREE_GIFT_CONFIG.threshold, and
 * auto-removes it if the subtotal drops back below that.
 *
 * FIX (Aug 2026): syncFreeGift() used to silently drop any call that
 * arrived while a previous sync was still in flight:
 *
 *   function syncFreeGift() {
 *     if (isSyncing) return;   // <-- just bails, nothing re-checks later
 *     ...
 *
 * On the very first add-to-cart after a page load, the DOMContentLoaded
 * sync (which does its own network round trips) is often still running
 * when the add-to-cart's own sync call comes in a moment later — that
 * second call, the one that should actually add the gift, would get
 * silently swallowed. Nothing was left to re-run it, so the cart stayed
 * without the gift until some *other* action (another add-to-cart, or a
 * refresh, which re-evaluates the real cart from scratch on the server)
 * happened to trigger a sync while nothing else was in flight. That
 * matches exactly what was reported: it "gets added eventually" but only
 * after a refresh or a second add, not on the qualifying action itself.
 *
 * Fix: a call that arrives mid-sync now sets a `needsResync` flag instead
 * of being dropped, and the whole check re-runs immediately after the
 * in-flight one finishes — so the qualifying action is never lost, it's
 * just queued behind whatever was already running (typically finishes in
 * well under a second, not "wait for the next unrelated action").
 *
 * Also fixed a related bug in the stale-gift cleanup branch: it used to
 * do `isSyncing = false; return syncFreeGift();`, but since syncFreeGift()
 * doesn't return its promise chain, the outer .finally() would reset
 * isSyncing again immediately — while the recursive re-check triggered by
 * that inner call was still in flight, opening a window for a second,
 * overlapping sync to start. That's replaced with a proper recursive
 * return of the same promise chain (runSyncCycle()).
 *
 * FIX #2 (same date, superseded by FIX #4 below): originally tried to fix
 * a cart-drawer re-render race by publishing our own `cart-update` event
 * after finishing a gift mutation. Removed — see FIX #4 for why that
 * didn't actually solve the race, and what does.
 *
 * FIX #3 (root cause of the *original* "only updates on refresh" report):
 * this theme's `window.routes` has cart_add_url = "/cart/add", cart_change_url
 * = "/cart/change", cart_update_url = "/cart/update" — NO ".js" suffix (many
 * Dawn forks use the plain routes now). The fetch-detection regex here
 * required ".js" in the URL, so it never matched a single real add-to-cart
 * request from product-form.js / cart.js — meaning syncFreeGift() was NEVER
 * triggered by the add itself, only by the next full page load's own
 * DOMContentLoaded check. The regex now matches the URL with or without ".js".
 *
 * FIX #4 (Aug 2026 — the "added, then disappears a second later" report,
 * diagnosed from a screen recording): after FIX #3, the gift *does* get
 * added and rendered correctly, and then a moment later gets visually
 * wiped back out even though the cart still qualifies. Root cause is a
 * race in the theme's own code, not this file's add/remove logic:
 *
 *   assets/product-form.js, right after a plain add-to-cart, publishes
 *   the theme's `cart-update` pub/sub event. assets/cart.js's
 *   CartItems.onCartUpdate() reacts to *any* such event (from any source)
 *   by firing its own independent fetch('/cart?section_id=cart-drawer')
 *   and doing `targetElement.replaceWith(sourceElement)` on the cart
 *   drawer — with no request-ordering guard at all. Whatever response
 *   lands last wins, unconditionally.
 *
 *   Our own sync needs two round trips (read the cart, then add/remove
 *   the gift) to do its job, so it usually finishes correctly — but that
 *   single-round-trip onCartUpdate() fetch can still be in flight when we
 *   finish, and land *after* us, overwriting our correct, gift-included
 *   render with its own stale, gift-less snapshot (captured before the
 *   gift existed). That's the flicker: nothing wrong with the add/remove
 *   itself, just a last-response-wins race in code we don't own.
 *
 *   Removing our own FIX #2 republish doesn't help here — it only adds a
 *   *third* racer, and can't retroactively cancel an onCartUpdate() fetch
 *   that was already in flight before we finished. There's also no event
 *   we can listen for when onCartUpdate() clobbers us — it doesn't publish
 *   anything when it's done, so we can't react to it after the fact.
 *
 *   Fix: a short trailing re-verification. After we successfully add or
 *   remove the gift, we re-run the same check again ~2.2s later — long
 *   enough to outlast that race — and correct anything that drifted back
 *   out from under us. syncFreeGift() is idempotent (a no-op if the cart
 *   already matches what it should be), so this trailing check costs one
 *   extra /cart.js read per mutation and does nothing if nothing needs
 *   fixing. See scheduleTrailingRecheck() below.
 *
 * FIX #5 (Aug 2026 — the REAL cause of the repeating "added, removed,
 * added again" oscillation; confirmed against a live cart, not guessed):
 * this store has a genuine server-side "Free Gift" automatic discount
 * already installed (an app / Shopify Function), separate from this
 * script, that discounts any cart line tagged `_free_gift: true` down to
 * Rs.0. That means `cart.items_subtotal_price` — which Shopify computes
 * from each line's *post-discount* price — already excludes the gift's
 * cost once it's in the cart. But subtotalExcludingGift() below was
 * subtracting `giftItem.original_line_price` (the gift's pre-discount
 * price, e.g. Rs.600) from that already-gift-excluded subtotal — double
 * subtracting it. So immediately after adding a Rs.600 gift to a cart
 * that legitimately totals exactly Rs.3000, the very next check computed
 * (3000 − 600) = Rs.2400, decided the cart no longer qualified, and
 * removed the gift it had just added. With the gift gone, the subtotal
 * read Rs.3000 again, qualified, and the gift got re-added — forever,
 * every time this ran. FIX #4's trailing recheck actually made this
 * *more* visible (one more oscillation per mutation) rather than fixing
 * it, since the bug was in the qualification math itself, not a timing
 * race — FIX #4 is still worth keeping as a defensive measure against
 * the drawer-refresh race it targets, but it was never the fix for this.
 *
 * Fix: use each gift-tagged line's actual `line_price` (post-discount —
 * Rs.0 in the normal case) instead of `original_line_price` when
 * subtracting it back out. `items_subtotal_price` already reflects that
 * discount, so subtracting the post-discount amount (usually 0) is the
 * correct undo — if the discount isn't active for some reason and the
 * line is charging its full price, this still subtracts exactly what was
 * actually charged, which is what "subtotal excluding the gift" means.
 *
 * Known limitation (not fixable from theme code): Shopify's dynamic
 * checkout / "Buy it now" button ({{ form | payment_button }}) goes
 * straight from click to checkout without calling /cart/add.js through
 * the page's own fetch, so this script never runs before that checkout
 * starts and the gift won't be applied on that path. Getting the gift
 * onto a pure Buy-it-now checkout requires a Shopify Function (an
 * automatic discount or Cart/Checkout transform) shipped via an app —
 * that's a separate piece of work from this theme fix.
 */
(function () {
  if (!window.FREE_GIFT_CONFIG || !window.FREE_GIFT_CONFIG.variantId) {
    console.warn('[free-gift] FREE_GIFT_CONFIG missing or incomplete — skipping.');
    return;
  }

  var CONFIG = window.FREE_GIFT_CONFIG;
  var GIFT_VARIANT_ID = Number(CONFIG.variantId);
  var THRESHOLD_CENTS = Number(CONFIG.thresholdCents);
  var INTERNAL_HEADER = 'X-Free-Gift-Internal';
  var TRAILING_RECHECK_MS = 2200;

  var isSyncing = false;
  var needsResync = false;
  var trailingRecheckTimer = null;
  var originalFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isInternal = !!(init && init.headers && init.headers[INTERNAL_HEADER]);
    var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
    var promise = originalFetch(input, init);

    if (isCartMutation && !isInternal) {
      promise.then(function () { syncFreeGift(); }).catch(function () {});
    }
    return promise;
  };

  function internalFetch(url, options) {
    options = options || {};
    options.headers = Object.assign({}, options.headers, { 'Content-Type': 'application/json' });
    options.headers[INTERNAL_HEADER] = '1';
    return originalFetch(url, options);
  }

  function getCart() {
    return internalFetch('/cart.js', { method: 'GET' }).then(function (r) { return r.json(); });
  }

  function findGiftItem(cart) {
    return cart.items.find(function (item) { return item.variant_id === GIFT_VARIANT_ID; });
  }

  function findStaleGiftItems(cart) {
    return cart.items.filter(function (item) {
      return item.properties && item.properties._free_gift === 'true' &&
        item.variant_id !== GIFT_VARIANT_ID;
    });
  }

  function subtotalExcludingGift(cart) {
    // Use line_price (post-discount — normally 0, since a server-side
    // "Free Gift" discount already zeroes these lines out), not
    // original_line_price. items_subtotal_price is itself post-discount,
    // so subtracting the pre-discount price double-counts the discount
    // and undercounts the real qualifying subtotal — see FIX #5 above.
    var giftItem = findGiftItem(cart);
    var giftLinePrice = giftItem ? giftItem.line_price : 0;
    var staleLinePrice = findStaleGiftItems(cart).reduce(function (sum, item) {
      return sum + item.line_price;
    }, 0);
    return cart.items_subtotal_price - giftLinePrice - staleLinePrice;
  }

  function getSectionsToRender() {
    var sections = [];
    if (document.getElementById('CartDrawer')) sections.push({ id: 'cart-drawer', selector: '#CartDrawer' });
    var iconBubble = document.getElementById('cart-icon-bubble');
    if (iconBubble) sections.push({ id: 'cart-icon-bubble', selector: '#cart-icon-bubble' });
    var cartItems = document.getElementById('main-cart-items');
    if (cartItems) sections.push({ id: cartItems.dataset.id || 'main-cart-items', selector: '#main-cart-items' });
    var cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) sections.push({ id: cartFooter.dataset.id || 'main-cart-footer', selector: '#main-cart-footer' });
    return sections;
  }

  function renderSections(sectionsToRender, data) {
    if (!data || !data.sections) return;
    sectionsToRender.forEach(function (s) {
      var html = data.sections[s.id];
      if (!html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var newContent = parsed.querySelector(s.selector);
      var target = document.querySelector(s.selector);
      if (newContent && target) target.innerHTML = newContent.innerHTML;
    });
  }

  function addGift(sectionsToRender) {
    return internalFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _free_gift: 'true' } }],
        sections: sectionsToRender.map(function (s) { return s.id; }),
        sections_url: window.location.pathname,
      }),
    }).then(function (r) { return r.json(); });
  }

  function removeGift(giftItem, sectionsToRender) {
    return internalFetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({
        id: giftItem.key, quantity: 0,
        sections: sectionsToRender.map(function (s) { return s.id; }),
        sections_url: window.location.pathname,
      }),
    }).then(function (r) { return r.json(); });
  }

  function clearStaleGifts(staleItems, sectionsToRender) {
    var chain = Promise.resolve();
    staleItems.forEach(function (item) {
      chain = chain.then(function () { return removeGift(item, sectionsToRender); });
    });
    return chain;
  }

  function syncFreeGift() {
    if (isSyncing) {
      needsResync = true;
      return;
    }
    isSyncing = true;
    runAndSettle();
  }

  // Runs one sync cycle and always resolves isSyncing/needsResync afterwards,
  // regardless of recursion depth (see runSyncCycle's stale-gift branch).
  function runAndSettle() {
    runSyncCycle().catch(function (err) {
      console.error('[free-gift] sync failed:', err);
    }).finally(finishSync);
  }

  function finishSync() {
    isSyncing = false;
    if (needsResync) {
      needsResync = false;
      isSyncing = true;
      runAndSettle();
    }
  }

  // Returns the full promise chain for one cycle (including any recursive
  // re-check after clearing stale gift lines) so callers can tell when the
  // cycle has *actually* finished, not just when the first hop resolved.
  function runSyncCycle() {
    return getCart().then(function (cart) {
      var sectionsToRender = getSectionsToRender();
      var staleItems = findStaleGiftItems(cart);

      if (staleItems.length) {
        return clearStaleGifts(staleItems, sectionsToRender).then(function () {
          return runSyncCycle();
        });
      }

      var giftItem = findGiftItem(cart);
      var qualifies = subtotalExcludingGift(cart) >= THRESHOLD_CENTS;

      if (qualifies && !giftItem) {
        return addGift(sectionsToRender).then(function (data) { onGiftMutated(sectionsToRender, data); });
      }
      if (!qualifies && giftItem) {
        return removeGift(giftItem, sectionsToRender).then(function (data) { onGiftMutated(sectionsToRender, data); });
      }
    });
  }

  // FIX #4: see the file header for the full explanation. In short: the
  // theme's own CartItems.onCartUpdate() can independently overwrite the
  // cart drawer with a stale (gift-less) snapshot shortly after we've
  // correctly added the gift, because it reacts to the *plain* add-to-cart
  // event too and has no way to know our own, slower, two-round-trip
  // update is still in flight or has already finished more recently.
  //
  // We can't intercept or cancel that fetch, and it doesn't tell us when
  // it's done — so instead, once we've made a change, we schedule one more
  // authoritative check a couple of seconds out. If nothing clobbered us,
  // this is a harmless no-op (one extra /cart.js read). If something did,
  // this puts the gift right back — without needing to know which
  // component caused it or when.
  function onGiftMutated(sectionsToRender, data) {
    renderSections(sectionsToRender, data);
    scheduleTrailingRecheck();
  }

  function scheduleTrailingRecheck() {
    if (trailingRecheckTimer) clearTimeout(trailingRecheckTimer);
    trailingRecheckTimer = setTimeout(function () {
      trailingRecheckTimer = null;
      syncFreeGift();
    }, TRAILING_RECHECK_MS);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      syncFreeGift();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFreeGift);
  } else {
    syncFreeGift();
  }
})();
